import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, ElementRef, effect, HostListener, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { LabelViewerComponent } from '../label-viewer/label-viewer.component';
import { MinimapIconComponent } from '../icons/minimap-icon/minimap-icon.component';
import { HighlightBeamIconComponent } from '../icons/highlight-beam-icon/highlight-beam-icon.component';
import { TooltipWrapperComponent } from '../tooltip-wrapper/tooltip-wrapper.component';
import { CosmeticOptions, CustomRuleStyle, Displayable, DisplayOptions } from '../../models/filter-models';
import { DisplayType } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { Rarity } from "../../models/enums";
import { CosmeticsGeneratorService } from '../../services/cosmetics-generator.service';
import { EquipmentTierService } from '../../services/tiering/equipment-tier.service';
import { FilterService } from '../../services/filter.service';
import { Equipment, Tier } from '../../models/item-data-models';
import { GenerationHelperService } from '../../services/generators/generation-helper.service';
import { FilterStoreService } from '../../services/filter-store.service';
import { SettingsService } from '../../services/settings.service';
import { labelPreviewBgImageStyle } from '../label-preview-backgrounds';
import { StylePickerDialogComponent } from '../style-picker/style-picker-dialog/style-picker-dialog.component';
import { StyleLibraryPickerDialogComponent } from '../style-picker/style-library-picker-dialog/style-library-picker-dialog.component';
import { CosmeticStyleClipboardService, StyleLibraryEntry } from '../../services/cosmetic-style-clipboard.service';

const LIBRARY_QUICK_PICK_COUNT = 7;

export interface LibraryQuickPickOption {
  entry: StyleLibraryEntry;
  options: CosmeticOptions;
  isCurrent: boolean;
}

@Component({
  selector: 'app-equipment-tier-picker',
  imports: [
    NgTemplateOutlet,
    MinimapIconComponent,
    HighlightBeamIconComponent,
    TooltipWrapperComponent,
    LabelViewerComponent,
    StylePickerDialogComponent,
    StyleLibraryPickerDialogComponent,
  ],
  templateUrl: './equipment-tier-picker.component.html',
  styleUrls: ['../rule-preview/label-preview-shared.scss', '../style-picker/style-picker.component.scss', '../style-picker/custom-rule-style-picker/custom-rule-style-picker.component.scss', './equipment-tier-picker.component.scss']
})
export class EquipmentTierPickerComponent implements OnInit, OnChanges {
  private readonly settings = inject(SettingsService);
  private readonly styleClipboard = inject(CosmeticStyleClipboardService);

  labelBackgroundImage = computed(() => labelPreviewBgImageStyle(this.settings.labelPreviewBg()));

  showQuickApplyLibrary = computed(
    () =>
      this.settings.quickApplyRecentlyCopiedStylesInItemTierPicker() &&
      this.styleClipboard.hasLibraryEntries()
  );

  @Input({ required: true }) equipmentType!: EquipmentType;
  @Input({ required: true }) active!: boolean | undefined;

  @Input() disabled: boolean = false;

  @Input() rareName?: string;
  @Input() exampleName?: string;
  @Input() stackSize?: number;

  @Input() defaultVisible?: boolean; // Override what's specified in metadata
  @Input() defaultHidden?: boolean;

  @Input() alignLeft = false;
  @Input() standalone = false;

  @Input() rarity?: Rarity = Rarity.Normal;
  @Input() isCurrency?: boolean;
  @Input() isPoe1Gem?: boolean;
  @Input() isWaystone?: boolean;
  @Input() sockets?: string;
  @Input() influence?: string;
  @Input() fractured?: boolean;
  @Input() veiled?: boolean;
  @Input() synthesised?: boolean;
  @Input() memoryStrands?: boolean;

  dialogOpen = false;
  stylePickerDialogOpen = false;
  libraryPickerOpen = false;

  libraryQuickOptions: LibraryQuickPickOption[] = [];

  cosmeticOptions: CosmeticOptions = new CosmeticOptions();
  tier?: Tier;
  equipment?: Equipment;
  hidden = false;

  tierOptions: Array<{ tier: Tier; cosmeticOptions: CosmeticOptions; hidden: boolean }> = [];

  hasCustomRuleStyle = false;
  customCosmeticOptions: CosmeticOptions = new CosmeticOptions();

  /** Dummy displayable for StylePickerDialog; cosmeticOptions mirror the seed style when opened. */
  dummyDisplayable: Displayable = {
    displayOptions: new DisplayOptions({
      displayType: DisplayType.Custom,
      active: true,
      hidden: false,
      cosmeticOptions: new CosmeticOptions(),
    }),
  };

  DisplayType = DisplayType;

  private initialFilterChangeCounter: number;

  constructor(
    private cosmeticsGeneratorService: CosmeticsGeneratorService,
    private helper: GenerationHelperService,
    private equipmentTierService: EquipmentTierService,
    private filterService: FilterService,
    private filterStoreService: FilterStoreService,
    private elementRef: ElementRef
  ) {
    // Capture the initial counter value - we'll only react to changes from this value
    this.initialFilterChangeCounter = this.filterStoreService.dirtyFilterChanged();

    // React to filter changes - this will trigger when switching filters
    // but not on initial render since we check if counter has actually changed
    effect(() => {
      const currentCounter = this.filterStoreService.dirtyFilterChanged();
      // Only update if counter has changed from initial value and equipmentType is set
      if (currentCounter !== this.initialFilterChangeCounter && this.equipmentType) {
        this.setCosmeticOptionsFromSelectedTier();
      }
    });
  }

  ngOnInit(): void {
    this.setDataFromSelectedEquipmentType();
    this.refreshCustomRuleStyleState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['equipmentType']) {
      // equipmentType is required and arrives before ngOnInit; resolve metadata first so downstream lookups
      // (e.g. refreshCustomRuleStyleState via this.equipment.ruleId) work on the first pass.
      this.setDataFromSelectedEquipmentType();
    }
    if (changes['equipmentType'] || changes['active']) {
      this.setCosmeticOptionsFromSelectedTier();
    }
  }

  setDataFromSelectedEquipmentType() {
    this.equipment = this.equipmentTierService.getMetadataForEquipmentType(this.equipmentType);
    // Only set when not explicitly set through input parameters
    if (this.defaultVisible === undefined) this.defaultVisible = this.equipment?.visibleWhenDisabled;
    if (this.defaultHidden === undefined) this.defaultHidden = this.equipment?.hiddenWhenDisabled;
  }

  setCosmeticOptionsFromSelectedTier() {
    this.tier = this.equipmentTierService.getEquipmentTier(this.equipmentType);
    const result = this.getCosmeticOptionsForTier(this.tier);
    const displayOptions = this.helper.getEquipmentTierDisplayOptionsByType(this.equipmentType, this.active);
    const actualResult = this.cosmeticsGeneratorService.getCosmeticOptionsFromDisplayOptions(displayOptions, this.exampleName);
    Object.assign(this.cosmeticOptions, actualResult.cosmeticOptions);
    this.hidden = result.hidden;

    this.refreshCustomRuleStyleState();
  }

  refreshCustomRuleStyleState() {
    const entry = this.findRuleStyle();
    this.hasCustomRuleStyle = !!entry;
    this.customCosmeticOptions = entry ? new CosmeticOptions({ ...entry.options }) : new CosmeticOptions();
  }

  findRuleStyle(): CustomRuleStyle | undefined {
    if (!this.equipment) return undefined;
    return this.filterStoreService.activeFilter().customRuleStyles.find(s => s.ruleId === this.equipment!.ruleId);
  }

  hasRuleStyle(): boolean {
    return !!this.findRuleStyle();
  }

  getCosmeticOptionsForTier(tier: Tier): { cosmeticOptions: CosmeticOptions; hidden: boolean } {
    // Always show tier-based options here so the dropdown row stays meaningful even when a customRuleStyle is set.
    const displayOptions = this.helper.getEquipmentTierDisplayOptionsByType(this.equipmentType, undefined, true);
    displayOptions.displayTypeTier = tier;
    const result = this.cosmeticsGeneratorService.getCosmeticOptionsFromDisplayOptions(displayOptions, this.exampleName);
    return result;
  }

  initializeTierOptions() {
    const allTiers = Object.values(Tier).filter(tier => tier !== Tier.FHidden) as Tier[];
    // Reverse order: E (Visible) first, then D, C, B, A, S (Purple) last
    const reversedTiers = allTiers.reverse();
    this.tierOptions = reversedTiers.map(tier => {
      const result = this.getCosmeticOptionsForTier(tier);
      return {
        tier,
        cosmeticOptions: result.cosmeticOptions,
        hidden: result.hidden
      };
    });
  }

  refreshLibraryQuickOptions() {
    const entries = this.styleClipboard.getLibraryEntries().slice(0, LIBRARY_QUICK_PICK_COUNT);
    this.libraryQuickOptions = entries.map(entry => {
      const options = new CosmeticOptions({ ...entry.options });
      return {
        entry,
        options,
        isCurrent: this.hasCustomRuleStyle && this.cosmeticOptionsMatch(this.customCosmeticOptions, options),
      };
    });
  }

  private cosmeticOptionsMatch(a: CosmeticOptions, b: CosmeticOptions): boolean {
    const keys = new Set([
      ...(Object.keys(a) as (keyof CosmeticOptions)[]),
      ...(Object.keys(b) as (keyof CosmeticOptions)[]),
    ]);
    for (const key of keys) {
      if ((a[key] ?? undefined) !== (b[key] ?? undefined)) {
        return false;
      }
    }
    return true;
  }

  openStylePickerDialog() {
    if (!this.disabled) {
      if (this.dialogOpen) {
        this.closeStylePickerDialog();
      } else {
        this.initializeTierOptions();
        this.refreshLibraryQuickOptions();
        this.dialogOpen = true;
      }
    }
  }

  selectTier(tier: Tier) {
    const hadCustomRuleStyle = this.removeCustomRuleStyleIfPresent();

    // Don't update if selecting the same tier and there was no custom style to clear
    if (this.tier === tier && !hadCustomRuleStyle) {
      this.closeStylePickerDialog();
      return;
    }

    if (this.tier === tier && hadCustomRuleStyle) {
      this.filterService.updateFilter();
      this.setCosmeticOptionsFromSelectedTier();
      this.closeStylePickerDialog();
      return;
    }

    // Update the equipment tier (same as drag & drop in equipment-tiers component);
    // also persists the filter, which picks up the removed custom rule style.
    this.filterService.updateEquipmentItemTier(this.equipmentType, tier);

    this.setCosmeticOptionsFromSelectedTier();

    this.closeStylePickerDialog();
  }

  private removeCustomRuleStyleIfPresent(): boolean {
    if (!this.equipment) return false;
    const filter = this.filterStoreService.activeFilter();
    const idx = filter.customRuleStyles.findIndex(s => s.ruleId === this.equipment!.ruleId);
    if (idx < 0) return false;
    filter.customRuleStyles.splice(idx, 1);
    return true;
  }

  openCustomStylePicker() {
    if (this.disabled || !this.equipment) return;

    const entry = this.findRuleStyle();
    const seed = entry ? new CosmeticOptions({ ...entry.options }) : new CosmeticOptions({ ...this.cosmeticOptions });

    this.dummyDisplayable.displayOptions.displayType = DisplayType.Custom;
    this.dummyDisplayable.displayOptions.active = this.active;
    this.dummyDisplayable.displayOptions.hidden = false;
    this.dummyDisplayable.displayOptions.cosmeticOptions = seed;

    this.stylePickerDialogOpen = true;
    this.closeStylePickerDialog();
  }

  onCustomStyleUpdated() {
    if (!this.equipment) return;
    const options = this.dummyDisplayable.displayOptions.cosmeticOptions ?? new CosmeticOptions();
    this.applyCustomRuleStyle(options);
  }

  private applyCustomRuleStyle(options: CosmeticOptions) {
    if (!this.equipment) return;

    const filter = this.filterStoreService.activeFilter();
    const opts = new CosmeticOptions({ ...options });
    const row: CustomRuleStyle = {
      ruleId: this.equipment.ruleId,
      custom: true,
      partial: false,
      options: opts,
    };
    const idx = filter.customRuleStyles.findIndex(s => s.ruleId === this.equipment!.ruleId);
    if (idx >= 0) {
      filter.customRuleStyles[idx] = row;
    } else {
      filter.customRuleStyles.push(row);
    }

    this.filterService.updateFilter();
    this.setCosmeticOptionsFromSelectedTier();
  }

  openQuickApplyLibraryPicker() {
    if (this.disabled || !this.showQuickApplyLibrary()) return;
    this.libraryPickerOpen = true;
    this.closeStylePickerDialog();
  }

  applyLibraryEntry(options: CosmeticOptions) {
    if (this.disabled) return;
    this.applyCustomRuleStyle(options);
    this.closeStylePickerDialog();
  }

  onLibraryStyleSelected(options: CosmeticOptions) {
    this.applyCustomRuleStyle(options);
    this.closeLibraryPicker();
  }

  closeLibraryPicker() {
    this.libraryPickerOpen = false;
  }

  onCustomRuleStyleCleared() {
    this.setCosmeticOptionsFromSelectedTier();
  }

  closeCustomStylePicker() {
    this.stylePickerDialogOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.libraryPickerOpen) return;
    if (this.dialogOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeStylePickerDialog();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    if (this.libraryPickerOpen) {
      this.closeLibraryPicker();
      return;
    }
    if (this.dialogOpen) {
      this.closeStylePickerDialog();
    }
  }

  closeStylePickerDialog() {
    this.dialogOpen = false;
  }
}
